// Implements: F-UIX-019
/**
 * The ceiling editor of ONE organization. The section mounts it keyed by organization id
 * (checklist 14), so a draft, a field's refusal or a "saved" line never follows the admin to
 * another organization, and a late answer for a previous one lands in its own cache entry.
 *
 * The bounds are checked before anything is sent (`parseCeilingDraft`); a save is sent once
 * (`retry: false`) and the server's refusal is shown with the draft kept. A stricter ceiling is
 * saved like any other: the server never refuses it for being below the person limits (N3), so
 * the editor explains the clamp instead of blocking it, and says whether the distribution really
 * exceeds the ceiling from the server's answer (computed after the write, 7.S13.10). The one
 * refusal is the budget floor (P3, L-043): a total below what the teams already have allocated
 * comes back as `VALIDATION` with the allocated total, shown as any other refusal.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import type { CeilingView } from "../../../types/ceiling";
import { CeilingForm } from "../components/CeilingForm";
import type { SaveOutcome } from "../components/CeilingForm";
import { useCeiling, useSaveCeiling } from "../hooks/useCeiling";
import { draftOf, parseCeilingDraft, tightens } from "../model/ceilingForm";
import type { CeilingDraft, CeilingField, FieldErrors } from "../model/ceilingForm";

function withoutField(errors: FieldErrors, field: CeilingField): FieldErrors {
  return Object.fromEntries(Object.entries(errors).filter(([key]) => key !== field));
}

interface DraftEditorProps {
  readonly organizationId: string;
  readonly stored: CeilingView;
}

/** The form over a ceiling that has been read: the draft starts from it. */
function DraftEditor({ organizationId, stored }: DraftEditorProps): ReactElement {
  const { t } = useTranslation();
  const save = useSaveCeiling(organizationId);
  const [draft, setDraft] = useState<CeilingDraft>(() => draftOf(stored));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saved, setSaved] = useState<SaveOutcome>(null);

  const parsed = parseCeilingDraft(draft);
  const tighter = parsed.ok && tightens(stored, parsed.body);

  return (
    <CeilingForm
      draft={draft}
      errors={errors}
      tighter={tighter}
      storedExceeds={stored.distribution_exceeds_ceiling}
      saving={save.isPending}
      refusal={refusal}
      saved={saved}
      onChange={(field, value) => {
        setDraft((current) => ({ ...current, [field]: value }));
        // The edited field's sentence goes; the others stay until the next save.
        setErrors((current) => (field in current ? withoutField(current, field) : current));
        setSaved(null);
      }}
      onSubmit={() => {
        setRefusal(null);
        setSaved(null);
        if (!parsed.ok) {
          setErrors(parsed.errors);
          return;
        }
        setErrors({});
        save.mutate(parsed.body, {
          onSuccess: (view) => {
            setDraft(draftOf(view));
            setSaved({ exceeds: view.distribution_exceeds_ceiling, tighter });
          },
          onError: (error) => {
            setRefusal(userMessage(error, t("admin.ceilings.failed")));
          },
        });
      }}
    />
  );
}

export interface CeilingEditorContainerProps {
  readonly organizationId: string;
}

export function CeilingEditorContainer({ organizationId }: CeilingEditorContainerProps): ReactElement {
  const { t } = useTranslation();
  const ceiling = useCeiling(organizationId);
  if (ceiling.isError) {
    return (
      <ErrorState
        error={ceiling.error}
        onRetry={() => {
          void ceiling.refetch();
        }}
      />
    );
  }
  if (ceiling.data === undefined) return <LoadingState label={t("admin.ceilings.loading")} />;
  return <DraftEditor organizationId={organizationId} stored={ceiling.data} />;
}
